import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { SERVER_URL } from "@/config/config";
import {
  equipItems,
  getEquipmentInfo,
  getCitiesTotalPower,
  getCityDeveloperCityIds,
  getIsCityDeveloper,
  getTotalPower,
} from "@/helper/contractFunctions/MafiaEquipment";
import { City } from "@/constants/const";
import { EquipmentsInfo } from "@/types/EquipmentsInfo";
import { WorthValueItem } from "@/types/WorthValueItem";

export interface EquipmentState {
  isGettingEquipmentInfo: boolean;
  isEquippingItems: boolean;
  isGettingTotalPower: boolean;
  isGettingCitiesTotalPower: boolean;
  equipmentsInfo: EquipmentsInfo;
  totalPower: number;
  totalDefense: number;
  citiesTotalPower: number[];
  citiesTotalDefense: number[];
  isGettingItemsInfo: boolean;
  citiesEquipmentInfo: EquipmentsInfo[];
  isGettingCitiesEquipmentInfo: boolean;
  worthValues: WorthValueItem;
  isLoadingWorthValues: boolean;
  isCityDeveloper: boolean;
  isGettingCityDeveloper: boolean;
  cityDeveloperCityIds: number[];
}

const initialState: EquipmentState = {
  isGettingEquipmentInfo: false,
  isEquippingItems: false,
  isGettingTotalPower: false,
  isGettingCitiesTotalPower: false,
  equipmentsInfo: {
    itemIds: [],
    mafiaAmount: 0,
    equippedAt: 0,
  },
  totalPower: 0,
  totalDefense: 0,
  citiesTotalPower: [],
  citiesTotalDefense: [],
  isGettingItemsInfo: false,
  citiesEquipmentInfo: [],
  isGettingCitiesEquipmentInfo: false,
  worthValues: {
    rankData: {
      rankNumber: 0,
      rankPercent: 0,
      rankWorth: 0,
    },
    roleData: {
      roleWorth: 0,
      roleName: "",
    },
    raceData: {
      raceXp: 0,
      raceXpWorth: 0,
    },
    bustOutData: {
      bustOutXp: 0,
      bustOutXpWorth: 0,
    },
    ksData: {
      ksXp: 0,
      ksXpWorth: 0,
    },
    business: {
      businessWorth: 0,
      businessTypeId: -1,
      businessCategoryId: -1,
    },
    realEstate: {
      slotSubType: 0,
      name: "",
      worth: 0,
    },
    activeLand: {
      activeLandCount: 0,
      activeLandWorth: 0,
    },
    Position: {
      rankByGlobal: 0,
      rankByFamily: 0,
    },
    cityDeveloper: {
      cityDeveloperWorth: 0,
      cityInfo: -1,
    },
    creditSpent: {
      creditSpent: 0,
      creditSpentWorth: 0,
    },
    mafia: {
      balance: 0,
      mafiaWorth: 0,
    },
    bulletData: {
      bulletAmount: 0,
      bulletWorth: 0,
    },
    cashData: {
      cashBalance: 0,
      cashWorth: 0,
    },
    cityPower: {
      totalDefensePower: 0,
      totalOffensePower: 0,
      offensePowerWorth: 0,
      defensePowerWorth: 0,
      cityPowerWorth: 0,
    },
  },

  isLoadingWorthValues: false,
  isCityDeveloper: false,
  isGettingCityDeveloper: false,
  cityDeveloperCityIds: [],
};

export const getEquipmentInfoAction = createAsyncThunk(
  "equipment/getEquipmentInfoAction",
  async ({
    account,
    cityId,
    signMsg,
    signature,
  }: {
    account: string;
    cityId: number;
    signMsg: string;
    signature: string;
  }) => {
    const equipmentsInfo = await getEquipmentInfo(
      account,
      cityId,
      signMsg,
      signature
    );
    return equipmentsInfo;
  }
);

export const getCitiesEquipmentInfoAction = createAsyncThunk(
  "equipment/getCitiesEquipmentInfoAction",
  async ({
    account,
    signMsg,
    signature,
  }: {
    account: string;
    signMsg: string;
    signature: string;
  }) => {
    const results = await Promise.all(
      City.map((_, cityId) =>
        getEquipmentInfo(account, cityId, signMsg, signature)
      )
    );
    return results as EquipmentsInfo[];
  }
);

export const equipItemsAction = createAsyncThunk(
  "equipment/equipItemsAction",
  async ({
    account,
    cityId,
    itemIds,
    delta,
  }: {
    account: string;
    cityId: number;
    itemIds: number[];
    delta: number;
  }) => {
    const tx = await equipItems(account, cityId, itemIds, delta);
    return tx.transactionHash;
  }
);

export const getTotalPowerAction = createAsyncThunk(
  "equipment/getTotalPowerAction",
  async ({
    account,
    cityId,
    signMsg,
    signature,
  }: {
    account: string;
    cityId: number;
    signMsg: string;
    signature: string;
  }) => {
    const totalPower = await getTotalPower(account, cityId, signMsg, signature);
    return totalPower;
  }
);

export const getCitiesTotalPowerAction = createAsyncThunk(
  "equipment/getCitiesTotalPowerAction",
  async ({
    account,
    signMsg,
    signature,
  }: {
    account: string;
    signMsg: string;
    signature: string;
  }) => {
    const totalPower = await getCitiesTotalPower(account, signMsg, signature);
    return totalPower;
  }
);

export const getIsCityDeveloperAction = createAsyncThunk(
  "equipment/getIsCityDeveloperAction",
  async ({ account, cityId }: { account: string; cityId: number }) => {
    return getIsCityDeveloper(account, cityId);
  }
);

export const getCityDeveloperCitiesAction = createAsyncThunk(
  "equipment/getCityDeveloperCitiesAction",
  async ({ account }: { account: string }) => {
    return getCityDeveloperCityIds(account);
  }
);

export const getWorthValues = createAsyncThunk(
  "equipment/getWorthValues",
  async ({
    account,
    signMsg,
    signature,
  }: {
    account: string;
    signMsg: string;
    signature: string;
  }) => {
    const response = await axios.get(`${SERVER_URL}/profile/worth/${account}`, {
      customHeaders: {
        signMsg,
        signature,
      },
    });

    return response.data as WorthValueItem;
  }
);

export const getItemsInfoAction = createAsyncThunk(
  "equipment/getItemsInfoAction",
  async ({ itemIds }: { itemIds: number[] }) => {
    const response = await axios.get(
      `${SERVER_URL}/profile/itemInfo?ids=${itemIds.join(",")}`
    );
    return response.data;
  }
);

const equipmentSlice = createSlice({
  name: "equipment",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(getEquipmentInfoAction.pending, (state) => {
      state.isGettingEquipmentInfo = true;
    });
    builder.addCase(getEquipmentInfoAction.fulfilled, (state, action) => {
      state.isGettingEquipmentInfo = false;
      state.equipmentsInfo = action.payload;
    });
    builder.addCase(getEquipmentInfoAction.rejected, (state) => {
      state.isGettingEquipmentInfo = false;
    });
    builder.addCase(equipItemsAction.pending, (state) => {
      state.isEquippingItems = true;
    });
    builder.addCase(getTotalPowerAction.pending, (state) => {
      state.isGettingTotalPower = true;
    });
    builder.addCase(equipItemsAction.fulfilled, (state, action) => {
      state.isEquippingItems = false;
    });
    builder.addCase(equipItemsAction.rejected, (state) => {
      state.isEquippingItems = false;
    });
    builder.addCase(getTotalPowerAction.fulfilled, (state, action) => {
      state.isGettingTotalPower = false;
      state.totalDefense = action.payload[0];
      state.totalPower = action.payload[1];
    });
    builder.addCase(getTotalPowerAction.rejected, (state) => {
      state.isGettingTotalPower = false;
    });
    builder.addCase(getCitiesTotalPowerAction.pending, (state) => {
      state.isGettingCitiesTotalPower = true;
    });
    builder.addCase(getCitiesTotalPowerAction.fulfilled, (state, action) => {
      state.isGettingCitiesTotalPower = false;
      state.citiesTotalPower = action.payload[1];
      state.citiesTotalDefense = action.payload[0];
    });
    builder.addCase(getCitiesTotalPowerAction.rejected, (state) => {
      state.isGettingCitiesTotalPower = false;
    });
    builder.addCase(getCitiesEquipmentInfoAction.pending, (state) => {
      state.isGettingCitiesEquipmentInfo = true;
    });
    builder.addCase(getCitiesEquipmentInfoAction.fulfilled, (state, action) => {
      state.isGettingCitiesEquipmentInfo = false;
      state.citiesEquipmentInfo = action.payload;
    });
    builder.addCase(getCitiesEquipmentInfoAction.rejected, (state) => {
      state.isGettingCitiesEquipmentInfo = false;
    });
    builder.addCase(getWorthValues.pending, (state) => {
      state.isLoadingWorthValues = true;
    });
    builder.addCase(getWorthValues.fulfilled, (state, { payload }) => {
      if (typeof payload === "object" && payload !== null) {
        state.worthValues = payload;
      }
      state.isLoadingWorthValues = false;
    });
    builder.addCase(getWorthValues.rejected, (state) => {
      state.isLoadingWorthValues = false;
    });

    builder.addCase(getIsCityDeveloperAction.pending, (state) => {
      state.isGettingCityDeveloper = true;
      state.isCityDeveloper = false;
    });
    builder.addCase(getIsCityDeveloperAction.fulfilled, (state, action) => {
      state.isGettingCityDeveloper = false;
      state.isCityDeveloper = action.payload;
    });
    builder.addCase(getIsCityDeveloperAction.rejected, (state) => {
      state.isGettingCityDeveloper = false;
      state.isCityDeveloper = false;
    });

    builder.addCase(getCityDeveloperCitiesAction.fulfilled, (state, action) => {
      state.cityDeveloperCityIds = action.payload;
    });
    builder.addCase(getCityDeveloperCitiesAction.rejected, (state) => {
      state.cityDeveloperCityIds = [];
    });

    builder.addCase(getItemsInfoAction.pending, (state) => {
      state.isGettingItemsInfo = true;
    });
    builder.addCase(getItemsInfoAction.fulfilled, (state, action) => {
      state.isGettingItemsInfo = false;
    });
    builder.addCase(getItemsInfoAction.rejected, (state) => {
      state.isGettingItemsInfo = false;
    });
  },
});

export default equipmentSlice.reducer;
